import { ReactElement, ReactNode } from 'react';
import { compact } from 'lodash-es';

import { TemplateForm, TemplateFormSubmitHandler } from '~/components/template-form';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

export type NotifyMeFormProps = {
  templateForm: TemplateFormType;
  onCancelClick?: () => void;
  onSubmit?: (formData: any) => void;
  resetOnSubmit?: boolean;
  formFieldElementsWrapper?: ReactElement;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  showToast?: boolean;
  onError?: () => void;
  successMessageSlot?: ReactNode;
  extraTrackEventProperties?: Record<string, any>;
  renderFormFields?: (formFields: ReactNode[]) => ReactNode;
};

export function NotifyMeForm({
  templateForm,
  onCancelClick,
  onSubmit,
  resetOnSubmit = true,
  formFieldElementsWrapper,
  isFullWidth,
  isDisabled,
  onError,
  successMessageSlot = null,
  extraTrackEventProperties,
  renderFormFields,
}: NotifyMeFormProps) {
  const { isSubmitting, sendSegmentLeadFormEvents } = useLeadFormEvent();
  const { user } = useUser();

  const handleNotifyContactForm: TemplateFormSubmitHandler = async ({ formData, resetForm }) => {
    const properties = transformFormDataToEventProperties(formData);
    const response = await sendSegmentLeadFormEvents({
      identifyTraits: {
        ...properties,
      },
      trackAnalyticsEvent: {
        name: 'Marketing Qualified Lead Form Submitted',
        type: 'submit',
        label: templateForm.submitButton ? templateForm.submitButton.buttonLabel : templateForm.submitButtonText ?? '',
        action: 'form_submitted',
        additionalAttributes: {
          ...extraTrackEventProperties,
          ...properties,
        },
      },
    });
    if (response.success) {
      onSubmit?.(formData);
      if (resetOnSubmit) {
        resetForm();
      }
    } else {
      onError?.();
    }
  };

  const email = user?.email ?? '';
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';
  const name = compact([firstName, lastName]).join(' ');

  return (
    <TemplateForm
      key={user?.id}
      formFieldElementsWrapper={formFieldElementsWrapper}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isSubmitting={isSubmitting}
      renderFormFields={renderFormFields}
      successMessageSlot={successMessageSlot}
      templateForm={templateForm}
      defaultValues={{
        email,
        firstName,
        lastName,
        name,
      }}
      onCancelClick={onCancelClick}
      onSubmit={handleNotifyContactForm}
    />
  );
}
